import { useEffect, useRef, useState } from "react";
import env from "@/env";
import { useAuthState } from "@/store/auth";
import { fetchUserDashboardMenuVisibility } from "@/utils/userDashboardMenu";

type UserDashboardMenuVisibility = {
  hasWorkers?: boolean;
  hasStaking?: boolean;
  isLoading: boolean;
};

export const useUserDashboardMenuVisibility = (isMenuOpen: boolean) => {
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");
  const hasFetchedRef = useRef(false);
  const [visibility, setVisibility] = useState<UserDashboardMenuVisibility>({
    isLoading: false
  });

  useEffect(() => {
    if (!isLoggedIn) {
      hasFetchedRef.current = false;
      setVisibility({ isLoading: false });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (
      env.ALWAYS_SHOW_USER_DASHBOARD_MENUS ||
      !isMenuOpen ||
      !isLoggedIn ||
      !env.EXPLORER_COMBINED ||
      hasFetchedRef.current
    ) {
      return;
    }

    hasFetchedRef.current = true;
    let cancelled = false;

    setVisibility({ isLoading: true });

    fetchUserDashboardMenuVisibility()
      .then(({ hasWorkers, hasStaking }) => {
        if (cancelled) {
          return;
        }
        setVisibility({ hasWorkers, hasStaking, isLoading: false });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setVisibility({ hasWorkers: false, hasStaking: false, isLoading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [isMenuOpen, isLoggedIn]);

  if (env.ALWAYS_SHOW_USER_DASHBOARD_MENUS && isLoggedIn) {
    return {
      hasWorkers: true,
      hasStaking: true,
      isLoading: false
    };
  }

  const shouldResolve =
    env.EXPLORER_COMBINED && isLoggedIn && isMenuOpen && !hasFetchedRef.current;

  return {
    hasWorkers: visibility.hasWorkers,
    hasStaking: visibility.hasStaking,
    isLoading: visibility.isLoading || shouldResolve
  };
};
